import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Checkbox,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Snackbar,
  FormControlLabel,
  Switch
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { soarService, firewallService } from '../services/api';
import { formatLocalDateTime } from '../utils/date';

// Validation schema for SOAR configuration
const soarValidationSchema = Yup.object({
  webhookUrl: Yup.string()
    .required('Webhook URL is required')
    .url('Please enter a valid webhook URL')
    .matches(/(http|https):\/\//, 'Webhook URL must start with http:// or https://'),
  protocol: Yup.string()
    .required('Protocol is required')
    .oneOf(['HTTP', 'HTTPS'], 'Invalid protocol'),
  method: Yup.string()
    .required('Method is required')
    .oneOf(['POST', 'PUT', 'GET'], 'Invalid method'),
  timeout: Yup.number()
    .required('Timeout is required')
    .typeError('Timeout must be a number')
    .min(1, 'Timeout must be at least 1 second')
    .max(300, 'Timeout cannot exceed 300 seconds'),
  retryAttempts: Yup.number()
    .required('Retry attempts is required')
    .typeError('Retry attempts must be a number')
    .min(0, 'Retry attempts cannot be negative')
    .max(10, 'Retry attempts cannot exceed 10'),
  verifySsl: Yup.boolean()
});

// Helper to get status chip color
const getStatusChipColor = (status) => {
  if (status === 'success') return 'success';
  if (status === 'failed') return 'error';
  if (status === 'pending') return 'warning';
  return 'default';
};

// Helper to get alert level from score - MATCHES SIEM TAB THRESHOLDS
const getAlertLevelFromScore = (score) => {
  if (score === null || score === undefined || isNaN(Number(score))) {
    return null;
  }
  const value = Number(score);
  if (value < 30) return 'Critical Alert';
  if (value < 50) return 'High Alert';
  if (value < 80) return 'Medium Alert';
  if (value < 96) return 'Low Alert';
  return 'Compliant';
};

const getAlertChipStyle = (alertLevel) => {
  switch (alertLevel) {
    case 'Critical Alert':
      return { backgroundColor: '#d32f2f', color: '#fff' };
    case 'High Alert':
      return { backgroundColor: '#f57c00', color: '#fff' };
    case 'Medium Alert':
      return { backgroundColor: '#fbc02d', color: 'rgba(0,0,0,0.87)' };
    case 'Low Alert':
      return { backgroundColor: '#388e3c', color: '#fff' };
    case 'Compliant':
      return { backgroundColor: '#0d47a1', color: '#fff' };
    default:
      return { backgroundColor: '#616161', color: '#fff' };
  }
};

function Soar() {
  // Configuration state
  const [soarConfig, setSoarConfig] = useState({
    webhookUrl: '',
    protocol: 'HTTPS',
    method: 'POST',
    timeout: 30,
    retryAttempts: 3,
    verifySsl: true
  });

  // SOAR history state
  const [soarHistory, setSoarHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0
  });
  const [selectedLogs, setSelectedLogs] = useState([]);

  // UI state
  const [testing, setTesting] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [detailsDialog, setDetailsDialog] = useState({ open: false, entry: null });
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    status: '',
    alertType: ''
  });

  // Formik setup
  const formik = useFormik({
    initialValues: soarConfig,
    validationSchema: soarValidationSchema,
    enableReinitialize: true,
    onSubmit: handleSaveConfig
  });

  // Fetch saved configuration on mount
  useEffect(() => {
    fetchSOARConfig();
    fetchSOARHistory();
  }, []);

  // Fetch SOAR history when pagination or filters change
  useEffect(() => {
    fetchSOARHistory();
  }, [pagination.page, filters]);

  const fetchSOARConfig = async () => {
    try {
      setLoadingConfig(true);
      const config = await soarService.getSOARConfig();
      if (config) {
        const loadedConfig = {
          webhookUrl: config.webhook_url || '',
          protocol: config.protocol || 'HTTPS',
          method: config.method || 'POST',
          timeout: config.timeout || 30,
          retryAttempts: config.retry_attempts || 3,
          verifySsl: config.verify_ssl !== false
        };
        setSoarConfig(loadedConfig);
        formik.setValues(loadedConfig);
      }
    } catch (err) {
      console.error('Error fetching SOAR config:', err);
    } finally {
      setLoadingConfig(false);
    }
  };

  async function handleSaveConfig(values) {
    try {
      const savedConfig = await soarService.saveSOARConfig(values);
      if (savedConfig) {
        const updatedConfig = {
          webhookUrl: savedConfig.webhook_url || '',
          protocol: savedConfig.protocol || 'HTTPS',
          method: savedConfig.method || 'POST',
          timeout: savedConfig.timeout || 30,
          retryAttempts: savedConfig.retry_attempts || 3,
          verifySsl: savedConfig.verify_ssl !== false
        };
        setSoarConfig(updatedConfig);
      }
      setSnackbar({
        open: true,
        message: 'SOAR configuration saved successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error saving SOAR config:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || 'Failed to save SOAR configuration',
        severity: 'error'
      });
    }
  }

  const handleTestWebhook = async () => {
    if (!formik.values.webhookUrl) {
      setSnackbar({
        open: true,
        message: 'Please enter a webhook URL',
        severity: 'error'
      });
      return;
    }

    try {
      setTesting(true);
      const response = await soarService.testSOARWebhook(formik.values);
      setSnackbar({
        open: true,
        message: `Webhook test successful! Response time: ${response.response_time}ms`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error testing webhook:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || 'Webhook test failed',
        severity: 'error'
      });
    } finally {
      setTesting(false);
    }
  };

  const handleClearForm = () => {
    formik.resetForm();
  };

  const fetchSOARHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await soarService.getSOARHistory(
        pagination.page,
        pagination.pageSize,
        filters
      );
      setSoarHistory(response.results || []);
      setPagination(prev => ({
        ...prev,
        total: response.total || 0,
        totalPages: response.total_pages || 0
      }));
      setSelectedLogs([]);
    } catch (err) {
      console.error('Error fetching SOAR history:', err);
      setSnackbar({
        open: true,
        message: 'Failed to load SOAR history',
        severity: 'error'
      });
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSelectLog = (logId) => {
    setSelectedLogs(prev =>
      prev.includes(logId)
        ? prev.filter(id => id !== logId)
        : [...prev, logId]
    );
  };

  const handleSelectAllLogs = (event) => {
    if (event.target.checked) {
      setSelectedLogs(soarHistory.map(log => log.id));
    } else {
      setSelectedLogs([]);
    }
  };

  const handleDeleteLogs = async () => {
    if (selectedLogs.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select logs to delete',
        severity: 'warning'
      });
      return;
    }

    if (window.confirm(`Delete ${selectedLogs.length} log(s)?`)) {
      try {
        await soarService.bulkDeleteSOARs(selectedLogs);
        setSnackbar({
          open: true,
          message: `${selectedLogs.length} log(s) deleted successfully`,
          severity: 'success'
        });
        fetchSOARHistory();
      } catch (err) {
        console.error('Error deleting logs:', err);
        setSnackbar({
          open: true,
          message: 'Failed to delete logs',
          severity: 'error'
        });
      }
    }
  };

  const handleRetryLog = async (logId) => {
    try {
      await soarService.retrySOAR(logId);
      setSnackbar({
        open: true,
        message: 'Webhook retry initiated successfully',
        severity: 'success'
      });
      fetchSOARHistory();
    } catch (err) {
      console.error('Error retrying webhook:', err);
      setSnackbar({
        open: true,
        message: 'Failed to retry webhook',
        severity: 'error'
      });
    }
  };

  const handleViewDetails = async (logId) => {
    try {
      const entry = await soarService.getSOAREntry(logId);
      setDetailsDialog({ open: true, entry });
    } catch (err) {
      console.error('Error fetching entry details:', err);
      setSnackbar({
        open: true,
        message: 'Failed to load entry details',
        severity: 'error'
      });
    }
  };

  const handleDeleteLog = async (logId) => {
    if (window.confirm('Delete this log entry?')) {
      try {
        await soarService.deleteSOAR(logId);
        setSnackbar({
          open: true,
          message: 'Log deleted successfully',
          severity: 'success'
        });
        fetchSOARHistory();
      } catch (err) {
        console.error('Error deleting log:', err);
        setSnackbar({
          open: true,
          message: 'Failed to delete log',
          severity: 'error'
        });
      }
    }
  };

  const handlePaginationChange = (event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
        SOAR Integration
      </Typography>

      {/* SOAR Configuration Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
          SOAR Webhook Configuration
        </Typography>

        {loadingConfig && <CircularProgress />}

        {!loadingConfig && (
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              {/* Webhook URL */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="webhookUrl"
                  name="webhookUrl"
                  label="Webhook URL"
                  placeholder="https://your-shuffle-instance.com/api/v1/workflows/..."
                  value={formik.values.webhookUrl}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.webhookUrl && Boolean(formik.errors.webhookUrl)}
                  helperText={formik.touched.webhookUrl && formik.errors.webhookUrl}
                />
              </Grid>

              {/* Protocol and Method Row */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={formik.touched.protocol && Boolean(formik.errors.protocol)}>
                  <InputLabel>Protocol</InputLabel>
                  <Select
                    id="protocol"
                    name="protocol"
                    value={formik.values.protocol}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Protocol"
                  >
                    <MenuItem value="HTTP">HTTP</MenuItem>
                    <MenuItem value="HTTPS">HTTPS</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={formik.touched.method && Boolean(formik.errors.method)}>
                  <InputLabel>Method</InputLabel>
                  <Select
                    id="method"
                    name="method"
                    value={formik.values.method}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Method"
                  >
                    <MenuItem value="POST">POST</MenuItem>
                    <MenuItem value="PUT">PUT</MenuItem>
                    <MenuItem value="GET">GET</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Timeout and Retry Attempts Row */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="timeout"
                  name="timeout"
                  label="Timeout (seconds)"
                  type="number"
                  inputProps={{ min: 1, max: 300 }}
                  value={formik.values.timeout}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.timeout && Boolean(formik.errors.timeout)}
                  helperText={formik.touched.timeout && formik.errors.timeout}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="retryAttempts"
                  name="retryAttempts"
                  label="Retry Attempts"
                  type="number"
                  inputProps={{ min: 0, max: 10 }}
                  value={formik.values.retryAttempts}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.retryAttempts && Boolean(formik.errors.retryAttempts)}
                  helperText={formik.touched.retryAttempts && formik.errors.retryAttempts}
                />
              </Grid>

              {/* Verify SSL Toggle */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      id="verifySsl"
                      name="verifySsl"
                      checked={formik.values.verifySsl}
                      onChange={formik.handleChange}
                    />
                  }
                  label="Verify SSL Certificate"
                />
              </Grid>

              {/* Action Buttons */}
              <Grid item xs={12} sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={!formik.isValid || formik.isSubmitting}
                >
                  Save Configuration
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleTestWebhook}
                  disabled={!formik.values.webhookUrl || testing}
                  startIcon={testing ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                >
                  {testing ? 'Testing...' : 'Test Webhook'}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleClearForm}
                >
                  Clear Form
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Paper>

      {/* JSON Payload Structure Display */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          JSON Payload Structure
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#0f172a', overflow: 'auto' }}>
          <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.875rem' }}>
            {JSON.stringify(
              {
                audit_time: '2024-01-31 14:30:45',
                firewall_ip: '192.168.1.1',
                device_name: 'Firewall-01',
                score: 85,
                alert_type: 'Medium Alert',
                failed_checks: ['Check 1', 'Check 2'],
                syslog_server: '192.168.1.100:514',
                report_url: 'https://be.nscat.securemango.com/audit-reports/view/{signed-token}'
              },
              null,
              2
            )}
          </pre>
        </Paper>
      </Paper>

      {/* SOAR History Section */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            SOAR History
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={fetchSOARHistory} size="small">
              <Tooltip title="Refresh">
                <RefreshIcon />
              </Tooltip>
            </IconButton>
            {selectedLogs.length > 0 && (
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteLogs}
              >
                Delete Selected ({selectedLogs.length})
              </Button>
            )}
          </Box>
        </Box>

        {/* Filters */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Start Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.startDate}
              onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="End Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.endDate}
              onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Alert Type</InputLabel>
              <Select
                value={filters.alertType}
                onChange={(e) => setFilters(prev => ({ ...prev, alertType: e.target.value }))}
                label="Alert Type"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Critical Alert">Critical Alert</MenuItem>
                <MenuItem value="High Alert">High Alert</MenuItem>
                <MenuItem value="Medium Alert">Medium Alert</MenuItem>
                <MenuItem value="Low Alert">Low Alert</MenuItem>
                <MenuItem value="Compliant">Compliant</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Table */}
        <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#0f172a' }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={soarHistory.length > 0 && selectedLogs.length === soarHistory.length}
                    indeterminate={selectedLogs.length > 0 && selectedLogs.length < soarHistory.length}
                    onChange={handleSelectAllLogs}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Audit Time</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Device Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Firewall IP</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Alert Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Score</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Response Time (ms)</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingHistory ? (
                <TableRow>
                  <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : soarHistory.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                    No SOAR webhook records found
                  </TableCell>
                </TableRow>
              ) : (
                soarHistory.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedLogs.includes(log.id)}
                        onChange={() => handleSelectLog(log.id)}
                      />
                    </TableCell>
                    <TableCell>{formatLocalDateTime(log.sent_at)}</TableCell>
                    <TableCell>{log.device_name || 'N/A'}</TableCell>
                    <TableCell>{log.firewall_ip || 'N/A'}</TableCell>
                    <TableCell>
                      {getAlertLevelFromScore(log.score)
                        ? <Chip label={getAlertLevelFromScore(log.score)} size="small" sx={{ ...getAlertChipStyle(getAlertLevelFromScore(log.score)), fontWeight: 600, fontSize: '0.7rem' }} />
                        : 'N/A'}
                    </TableCell>
                    <TableCell>{log.score !== null && log.score !== undefined ? log.score : 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                        label={log.status || 'unknown'}
                        color={getStatusChipColor(log.status)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{log.response_time || '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(log.id)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {log.status === 'failed' && (
                          <Tooltip title="Retry">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleRetryLog(log.id)}
                            >
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteLog(log.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {soarHistory.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={pagination.totalPages}
              page={pagination.page}
              onChange={handlePaginationChange}
              color="primary"
            />
          </Box>
        )}
      </Paper>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialog.open}
        onClose={() => setDetailsDialog({ open: false, entry: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Webhook Details</DialogTitle>
        <DialogContent>
          {detailsDialog.entry && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Chip label={detailsDialog.entry.status} color={getStatusChipColor(detailsDialog.entry.status)} />
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">
                  Device Name
                </Typography>
                <Typography>{detailsDialog.entry.device_name || 'N/A'}</Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">
                  Firewall IP
                </Typography>
                <Typography>{detailsDialog.entry.firewall_ip || 'N/A'}</Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">
                  Audit Score
                </Typography>
                <Typography>{detailsDialog.entry.score !== null && detailsDialog.entry.score !== undefined ? detailsDialog.entry.score : 'N/A'}</Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">
                  Response Time (ms)
                </Typography>
                <Typography>{detailsDialog.entry.response_time || '-'}</Typography>
              </Box>

              {detailsDialog.entry.error_message && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Error Message
                  </Typography>
                  <Alert severity="error">{detailsDialog.entry.error_message}</Alert>
                </Box>
              )}

              {detailsDialog.entry.payload && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Payload
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#0f172a', overflow: 'auto' }}>
                    <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {(() => {
                        try {
                          return JSON.stringify(JSON.parse(detailsDialog.entry.payload), null, 2);
                        } catch {
                          // Handle Python str() output (single quotes) or other non-JSON formats
                          try {
                            return JSON.stringify(JSON.parse(detailsDialog.entry.payload.replace(/'/g, '"')), null, 2);
                          } catch {
                            return detailsDialog.entry.payload;
                          }
                        }
                      })()}
                    </pre>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog({ open: false, entry: null })}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Soar;
